import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import Button from "../../common/Button";
import api from "../../../api";
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/material';

export default function PendingCollageRequest() {
  const [pendingCollages, setPendingCollages] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]); // To store selected rows
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editedCollege, setEditedCollege] = useState({
    name: '',
    email: '',
    address: ''
  });

  // Fetch pending colleges
  useEffect(() => {
    const getApiData = async () => {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}colleges/pending`
      );
      setPendingCollages(data);
    };
    getApiData();
  }, []);

  // Approve a single college
  const handleApprove = async (id) => {
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}colleges/approve/${id}`);
      alert("College Approved Successfully!");
      setPendingCollages((prev) => prev.filter((college) => college._id !== id));
    } catch (error) {
      console.error("Approval Failed:", error);
    }
  };

  // Approve multiple selected colleges
  const handleApproveMultiple = async () => {
    try {
      await Promise.all(selectedRows.map((id) =>
        api.put(`${process.env.REACT_APP_API_BASE_URL}colleges/approve/${id}`)
      ));
      alert("Selected Colleges Approved Successfully!");
      setPendingCollages((prev) => prev.filter((college) => !selectedRows.includes(college._id)));
      setSelectedRows([]); // Clear the selected rows after approval
    } catch (error) {
      console.error("Approval Failed:", error);
    }
  };

  // Delete College
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this college?");
    if (!confirmDelete) return;

    try {
      await api.delete(`${process.env.REACT_APP_API_BASE_URL}colleges/delete/${id}`);
      alert("College Deleted Successfully!");
      setPendingCollages((prev) => prev.filter((college) => college._id !== id));
    } catch (error) {
      console.error("Delete Failed:", error);
    }
  };

  // Open the Edit Modal and populate it with the selected college's data
  const handleEdit = async (id) => {
    const college = pendingCollages.find(college => college._id === id);
    setSelectedCollege(college);
    setEditedCollege({
      name: college.name,
      email: college.email,
      address: college.address
    });
    setOpenEditModal(true);
  };

  // Handle the changes in the edit form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedCollege((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  // Submit the updated college details
  const handleSubmit = async () => {
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}colleges/edit/${selectedCollege._id}`, editedCollege);
      alert("College Updated Successfully!");
      setPendingCollages((prev) => 
        prev.map((college) => 
          college._id === selectedCollege._id ? { ...college, ...editedCollege } : college
        )
      );
      setOpenEditModal(false);
    } catch (error) {
      console.error("Edit Failed:", error);
    }
  };

  const columns = [
    {
      field: "checkbox",
      headerName: "Select",
      width: 100,
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(params.row._id)}
          onChange={() => handleSelectRow(params.row._id)}
        />
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 2,
      minWidth: 150,
      headerClassName: "custom-header",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 3,
      minWidth: 200,
      headerClassName: "custom-header",
    },
    {
      field: "address",
      headerName: "Address",
      flex: 3,
      minWidth: 100,
      headerClassName: "custom-header",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 3,
      minWidth: 200,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <div className="flex space-x-2">
          <Button onClick={() => handleApprove(params.row._id)}>Approve</Button>
          <Button onClick={() => handleEdit(params.row._id)}>Edit</Button>
          <Button onClick={() => handleDelete(params.row._id)}>Delete</Button>
        </div>
      ),
    },
  ];

  // Handle the selection of rows
  const handleSelectRow = (id) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(id)) {
        return prevSelectedRows.filter((rowId) => rowId !== id);
      } else {
        return [...prevSelectedRows, id];
      }
    });
  };

  return (
    <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-white/10">
      <div className="mb-4">
        <Button onClick={handleApproveMultiple} disabled={selectedRows.length === 0}>Approve Selected</Button>
      </div>
      <DataGrid
        rows={pendingCollages || []}
        columns={columns}
        getRowId={(row) => row._id}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        pageSizeOptions={[10, 15]}
        sx={{
          // Glassy root container
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          border: "none",
          borderRadius: 3,
          backdropFilter: "blur(8px)",
        
          // Top header row
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
          },
        
          // Footer container (bottom row)
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "rgba(255,255,255,0.05)",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          },
        
          // Ensure rows have no override from CSS variable
          "& .MuiDataGrid-row": {
            backgroundColor: "transparent !important",
          },
        
          // Optional: override containerBackground if needed
          "--DataGrid-containerBackground": "transparent",
        }}
      />

      {/* Edit Modal */}
      <Modal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        aria-labelledby="edit-college-modal"
        aria-describedby="edit-college-modal-description"
      >
        <Box sx={{ width: 400, padding: 2, backgroundColor: 'white', margin: 'auto', marginTop: '10%' }}>
          <h2>Edit College</h2>
          <TextField
            label="Name"
            name="name"
            value={editedCollege.name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            value={editedCollege.email}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Address"
            name="address"
            value={editedCollege.address}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <div className="flex justify-between mt-3">
            <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Save</Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}
